import type { LifecycleStateLabel } from "@/api/projects";

/**
 * The one human spelling of each lifecycle label.
 *
 * Extracted from `run-state-badge.tsx`'s `STATE_META`, which owned it while the
 * badge was the only surface that said a state out loud. The lifecycle rail
 * (`run-lifecycle-rail.ts`) is the second, and it is a PURE module — it must be
 * able to name a stage without pulling framer-motion and lucide into its import
 * graph, and, more to the point, "staging inputs" reading as `staging_inputs`
 * in one place and "staging inputs" in the other is the drift a second copy
 * produces silently.
 *
 * The keys are the wire labels; the values are display text and nothing else.
 * Never map a wire label onto a DIFFERENT vocabulary here (INDEX.md invariant
 * #3, never invent a run-state string): this substitutes an underscore for a
 * space and expands nothing.
 */
export const STATE_LABEL: Record<LifecycleStateLabel, string> = {
  validating: "validating",
  cached: "cached",
  staging_inputs: "staging inputs",
  dispatched: "dispatched",
  running: "running",
  collecting_outputs: "collecting outputs",
  validating_outputs: "validating outputs",
  completed: "completed",
  failed_validation: "failed validation",
  failed_system: "failed system",
  failed_scientific: "failed scientific",
  failed_contract: "failed contract",
};

/**
 * The SOLID lifecycle fill, for a surface that marks a state with a shape too
 * small to hold the badge — the DAG tile's 3px left stripe, the phase lane's
 * step dot.
 *
 * It lives beside {@link STATE_LABEL} for the same reason that one does:
 * `run-state-badge.tsx` owned this map while the DAG tile was its only other
 * reader, and pulling framer-motion + lucide in is not a price a lean surface
 * should pay to name a colour. Two hand-kept copies of a twelve-key `Record`
 * drift, and the key that drifts is always the one nobody looks at.
 *
 * **A fill is never the only cue.** Colour alone is unreadable to a
 * colour-deficient viewer, so every adopter pairs this with the state said out
 * loud — `RunStateBadge` beneath the tile, an `sr-only` span beside the dot.
 */
export const STATE_STRIPE: Record<LifecycleStateLabel, string> = {
  validating: "bg-state-validating",
  cached: "bg-state-cached",
  staging_inputs: "bg-state-staging",
  dispatched: "bg-state-dispatched",
  running: "bg-state-running",
  collecting_outputs: "bg-state-collecting",
  validating_outputs: "bg-state-validating-outputs",
  completed: "bg-state-completed",
  failed_validation: "bg-state-failed-validation",
  failed_system: "bg-state-failed-system",
  failed_contract: "bg-state-failed-contract",
  failed_scientific: "bg-state-failed-scientific",
};
